import React, { useEffect, useState } from 'react'
import { getRequest, patchRequest, staticOptions } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import SaveNotification from 'src/components/common/SaveNotification'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Info } from 'react-feather'
import {
  getInputType,
  getCharacterLimit,
  validateConfig,
  formatConfigForBackend,
  parseShippingConfig,
  mapShippingProvider,
  getMerchantAttributesPayload
} from 'src/lib/utils/integrationsHelpers'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import useSWR from 'swr'
import {
  ShippingErrorsType,
  ShippingField,
  ShippingIntegrationConfigType,
  ShippingProvider
} from 'src/lib/types/integrations'
import { setMerchantAttributes } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'

interface GenericShippingProviderProps {
  provider: ShippingProvider
}

const fetcher = (url: string) => getRequest(url, apiURI.SHIPPING_ENGINE)

const ShippingIntegrationConfig: React.FC<GenericShippingProviderProps> = ({ provider }) => {
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()

  const [initialConfig, setInitialConfig] = useState<ShippingIntegrationConfigType>({
    enabled: false,
    provider: provider,
    fields: [],
    values: {}
  })
  const [currentConfig, setCurrentConfig] = useState<ShippingIntegrationConfigType>({
    enabled: false,
    provider: provider,
    fields: [],
    values: {}
  })

  const [errors, setErrors] = useState<ShippingErrorsType>({})
  const [settingsUpdated, setSettingsUpdated] = useState(false)

  const configFetchURL = `/utility/external-etd-config-providers/${provider}/config`
  const shippingConfigURL = `/merchant-shipping-configuration`

  const {
    data: shippingConfig,
    error: configError,
    isLoading: configLoading
  } = useSWR(shippingConfigURL, fetcher, staticOptions)

  const {
    data: uiFieldResponse,
    error: uiError,
    isLoading: uiLoading
  } = useSWR<any>(configFetchURL, fetcher, staticOptions)

  useEffect(() => {
    if (!shippingConfig || !uiFieldResponse) return

    const rawConfig = shippingConfig?.data || {}
    const uiFields = uiFieldResponse.data?.field_details || []

    const fullConfig = parseShippingConfig(rawConfig, uiFields, provider)

    setInitialConfig(fullConfig)
    setCurrentConfig(fullConfig)
  }, [shippingConfig, uiFieldResponse, provider])

  useEffect(() => {
    setSettingsUpdated(JSON.stringify(initialConfig) !== JSON.stringify(currentConfig))
  }, [initialConfig, currentConfig])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        resolve(true)
      } else reject(false)
    })
  })

  const saveConfig = async () => {
    const { errors, hasErrors } = validateConfig(currentConfig)
    setErrors(errors)
    if (hasErrors) return

    setLoaderProperties({ isLoaderVisible: true })

    try {
      const payload = formatConfigForBackend(currentConfig)

      const promises: Promise<unknown>[] = [
        patchRequest('/merchant-shipping-configuration', apiURI.SHIPPING_ENGINE, payload)
      ]

      if (!currentConfig.enabled) {
        const additionalPayload = getMerchantAttributesPayload(merchant)
        promises.push(setMerchantAttributes(additionalPayload, merchant?.accountId ?? ''))
      }

      const responses: any[] = await Promise.all(promises)

      if (responses?.[0]?.error) {
        throw new Error(responses[0]?.error)
      }

      if (responses?.[1]) setMerchantData(responses[1]?.data)

      const savedConfig: ShippingIntegrationConfigType = {
        ...currentConfig
      }

      setInitialConfig(savedConfig)
      successToast('Changes saved')
    } catch (e: any) {
      errorToast(e.message || 'Failed to save config')
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const updateField = (name: string, value: string | boolean) => {
    if (name === 'enabled') {
      setCurrentConfig(prevConfig => ({
        ...prevConfig,
        enabled: value as boolean
      }))
    } else {
      setCurrentConfig(prevConfig => ({
        ...prevConfig,
        values: {
          ...prevConfig.values,
          [name]: value
        }
      }))
    }

    setErrors(prev => ({
      ...prev,
      [name]: { error: '', errorStatus: false }
    }))
  }

  const handleDiscard = () => {
    setCurrentConfig(initialConfig)
    setSettingsUpdated(false)
  }

  if (configLoading || uiLoading) {
    return (
      <div className='animate-pulse px-8 py-8 bg-white rounded-lg flex flex-col gap-8'>
        <div className='h-12 w-full bg-gray-200 rounded mb-4' />
        <div className='flex justify-between gap-4'>
          <div className='flex-1'>
            <div className='h-60 bg-gray-200 rounded' />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <LayoutContainer>
        <div className='w-full flex items-center'>
          <div className='w-full flex flex-wrap'>
            <div className='w-full md:w-11/12 flex items-center'>
              <p className='text-sm text-gray-600'>{mapShippingProvider(provider)} is</p>
              <p className='text-base font-medium text-gray-900 ml-2'>
                {currentConfig?.enabled ? 'enabled' : 'disabled'}
              </p>
            </div>
            <div className='w-full md:w-1/12 flex items-center justify-end'>
              <FloCheckbox
                type='SWITCH'
                value={(currentConfig?.enabled as boolean) ?? false}
                setValue={(checked: boolean) => updateField('enabled', checked)}
              />
            </div>
          </div>
        </div>
      </LayoutContainer>

      <LayoutContainer heading='Credentials'>
        <div className='w-full px-2 py-3 flex flex-col gap-4'>
          <div className='flex items-center gap-[10px] border-[1px] border-yellow-faint text-yellow-light rounded-lg w-fit px-[10px] py-2 bg-yellow-faintest'>
            <Info size={16} color='#C7981F' />
            <p className='text-[14px]'>{t('shipping_info')}</p>
          </div>

          {currentConfig?.fields.map((item: ShippingField, index: number) => (
            <InputTextField
              key={item.name}
              textFieldId={index.toString()}
              label={item.display_name}
              isRequired={item.required}
              inputType={getInputType(item.name)}
              autoComplete='new-password'
              inputValue={currentConfig.values[item.name]}
              handleChange={e => updateField(item.name, e.target.value)}
              handleFocus={() => setErrors(prev => ({ ...prev, [item.name]: { error: '', errorStatus: false } }))}
              fixedCharacterLimit={getCharacterLimit(item.name)}
              errorStatus={errors?.[item.name]?.errorStatus ?? false}
              errorMessage={errors?.[item.name]?.error ?? ''}
              inputProps={{
                autoComplete: 'new-password',
                'data-lpignore': 'true',
                'data-form-type': 'other',
                form: { autoComplete: 'off' }
              }}
              customErrorMessage=''
            />
          ))}
        </div>
      </LayoutContainer>

      <SaveNotification isVisible={settingsUpdated} onSave={saveConfig} onDiscard={handleDiscard} className='w-full' />
    </>
  )
}

export default ShippingIntegrationConfig
